import { formaterJour } from '@admin-pmvb/core'
import { Link } from '@tanstack/react-router'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { type RefObject, useEffect, useRef, useState } from 'react'
import type { FicheTache } from '../api'
import { LIBELLES_TYPE_PIECE } from '../libelles'
import { dessinerPage, ouvrirPdf } from '../pdf'

// Aperçu des pièces d'une tâche : la première page en vignette, la pièce entière dans une
// visionneuse de l'appli (même rendu sur ordinateur et dans la PWA), et le mail d'où elle
// vient quand on le connaît. Sur la fiche d'une pièce, le document entier en regard.

type PieceTache = FicheTache['pieces'][number]

/** Fichier montré dans la visionneuse : une pièce, ou une pièce jointe d'un mail. */
export type FichierVu = { url: string; nom: string; typeMime: string }

const PAGES_MAX = 30
const fichier = (id: string) => `/api/pieces/${id}/fichier`
const estPdf = (f: { typeMime: string }) => f.typeMime === 'application/pdf'
const estImage = (f: { typeMime: string }) => f.typeMime.startsWith('image/')
const estXml = (f: { typeMime: string }) => f.typeMime.includes('xml')
const fichierDePiece = (p: PieceTache): FichierVu => ({
  url: fichier(p.id),
  nom: p.nomFichier ?? 'Pièce',
  typeMime: p.typeMime,
})

/** Bouton qui ouvre un mail dans Gmail (nouvel onglet, ou l'appli Gmail sur téléphone). */
export function BoutonMail({ lien, discret = false }: { lien: string; discret?: boolean }) {
  return (
    <a
      className={discret ? 'bouton-discret' : 'bouton'}
      href={lien}
      target="_blank"
      rel="noreferrer"
    >
      Ouvrir le mail
    </a>
  )
}

/** Charge un PDF ; null tant qu'il charge, 'erreur' s'il est illisible. */
function usePdf(url: string, actif: boolean) {
  const [doc, setDoc] = useState<PDFDocumentProxy | 'erreur' | null>(null)
  useEffect(() => {
    if (!actif) return
    let fini = false
    const pdf = ouvrirPdf(url)
    pdf.doc.then(
      (d) => !fini && setDoc(d),
      () => !fini && setDoc('erreur'),
    )
    return () => {
      fini = true
      pdf.fermer()
      setDoc(null)
    }
  }, [url, actif])
  return doc
}

function Page({
  doc,
  numero,
  largeur,
}: {
  doc: PDFDocumentProxy
  numero: number
  largeur: number
}) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const [erreur, setErreur] = useState(false)
  useEffect(() => {
    if (!canvas.current) return
    const rendu = dessinerPage(doc, numero, canvas.current, largeur)
    rendu.promesse.catch((e: unknown) => {
      if (!(e instanceof Error && e.name === 'RenderingCancelledException')) setErreur(true)
    })
    return rendu.annuler
  }, [doc, numero, largeur])
  if (erreur) return <p className="discret">Page {numero} illisible.</p>
  return <canvas ref={canvas} className="apercu-page" aria-label={`Page ${numero}`} />
}

export function Vignette({ piece }: { piece: PieceTache }) {
  const doc = usePdf(fichier(piece.id), estPdf(piece))
  if (!estPdf(piece)) return <span className="apercu-format">XML</span>
  if (doc === 'erreur') return <span className="apercu-format">Aperçu indisponible</span>
  if (!doc) return <span className="apercu-format">Chargement…</span>
  return <Page doc={doc} numero={1} largeur={220} />
}

/** Contenu d'un fichier XML (Factur-X, UBL…), tel quel. */
function TexteXml({ url }: { url: string }) {
  const [texte, setTexte] = useState<string | null>(null)
  useEffect(() => {
    let fini = false
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then(
        (t) => !fini && setTexte(t),
        () => !fini && setTexte(''),
      )
    return () => {
      fini = true
    }
  }, [url])
  if (texte === null) return <p className="discret">Chargement…</p>
  if (texte === '') return <p className="alerte">Fichier indisponible.</p>
  return <pre className="apercu-xml">{texte}</pre>
}

/** Visionneuse plein écran : PDF dessiné par pdf.js, image, ou XML en texte. */
export function Visionneuse({ f, onFermer }: { f: FichierVu; onFermer: () => void }) {
  const dialogue = useRef<HTMLDialogElement>(null)
  const corps = useRef<HTMLDivElement>(null)
  const doc = usePdf(f.url, estPdf(f))
  const [largeur, setLargeur] = useState(0)

  useEffect(() => {
    dialogue.current?.showModal()
    const mesurer = () => setLargeur(Math.min(corps.current?.clientWidth ?? 800, 900))
    mesurer()
    window.addEventListener('resize', mesurer)
    return () => window.removeEventListener('resize', mesurer)
  }, [])

  const pages = doc && doc !== 'erreur' ? Math.min(doc.numPages, PAGES_MAX) : 0
  return (
    <dialog ref={dialogue} className="visionneuse" onClose={onFermer}>
      <div className="visionneuse-barre">
        <strong className="visionneuse-titre">{f.nom}</strong>
        <a className="bouton-discret hors-pwa" href={f.url} target="_blank" rel="noreferrer">
          Nouvel onglet
        </a>
        <button type="button" className="bouton" onClick={() => dialogue.current?.close()}>
          Fermer
        </button>
      </div>
      <div ref={corps} className="visionneuse-corps">
        {estXml(f) && <TexteXml url={f.url} />}
        {estImage(f) && <img className="apercu-page" src={f.url} alt={f.nom} />}
        {!estPdf(f) && !estXml(f) && !estImage(f) && (
          <p className="discret">Pas d’aperçu pour ce type de fichier : l’enregistrer.</p>
        )}
        {estPdf(f) && doc === null && <p className="discret">Chargement…</p>}
        {doc === 'erreur' && <p className="alerte">Aperçu indisponible.</p>}
        {doc &&
          doc !== 'erreur' &&
          largeur > 0 &&
          Array.from({ length: pages }, (_, i) => (
            <Page key={i + 1} doc={doc} numero={i + 1} largeur={largeur} />
          ))}
        {doc && doc !== 'erreur' && doc.numPages > PAGES_MAX && (
          <p className="discret">
            {PAGES_MAX} pages sur {doc.numPages} : la suite dans un nouvel onglet.
          </p>
        )}
      </div>
    </dialog>
  )
}

/** Largeur utile d'un élément, suivie quand elle change (arrondie : pas de rendu à chaque pixel). */
function useLargeur(element: RefObject<HTMLElement | null>) {
  const [largeur, setLargeur] = useState(0)
  useEffect(() => {
    const e = element.current
    if (!e) return
    const mesurer = () => setLargeur(Math.floor(e.clientWidth / 20) * 20)
    mesurer()
    const observateur = new ResizeObserver(mesurer)
    observateur.observe(e)
    return () => observateur.disconnect()
  }, [element])
  return largeur
}

/**
 * Les pages d'un fichier à la largeur de leur cadre, défilantes : PDF dessiné par pdf.js, image, ou
 * XML en texte (aperçu d'une candidate dans le bilan des justificatifs).
 */
export function PagesDuFichier({ f }: { f: FichierVu }) {
  const corps = useRef<HTMLDivElement>(null)
  const largeur = useLargeur(corps)
  const doc = usePdf(f.url, estPdf(f))
  const pages = doc && doc !== 'erreur' ? Math.min(doc.numPages, PAGES_MAX) : 0
  return (
    <div ref={corps} className="pages-du-fichier">
      {estXml(f) && <TexteXml url={f.url} />}
      {estImage(f) && <img className="apercu-page" src={f.url} alt={f.nom} />}
      {!estPdf(f) && !estXml(f) && !estImage(f) && (
        <p className="discret">Pas d’aperçu pour ce type de fichier : le télécharger.</p>
      )}
      {estPdf(f) && doc === null && <p className="discret">Chargement…</p>}
      {doc === 'erreur' && <p className="alerte">Aperçu indisponible.</p>}
      {doc &&
        doc !== 'erreur' &&
        largeur > 0 &&
        Array.from({ length: pages }, (_, i) => (
          <Page key={i + 1} doc={doc} numero={i + 1} largeur={largeur} />
        ))}
      {doc && doc !== 'erreur' && doc.numPages > PAGES_MAX && (
        <p className="discret">
          {PAGES_MAX} pages sur {doc.numPages} : la suite dans un nouvel onglet.
        </p>
      )}
    </div>
  )
}

/**
 * Aperçu d'une pièce dans sa fiche : toutes ses pages à la largeur du cadre, défilantes, avec
 * la visionneuse plein écran à portée.
 */
export function ApercuDocument({
  f,
  plage,
}: {
  f: FichierVu
  /** Facture d'un fichier qui en regroupe plusieurs : ses pages seulement (le fichier reste entier). */
  plage?: { debut: number; fin: number } | null
}) {
  const corps = useRef<HTMLDivElement>(null)
  const largeur = useLargeur(corps)
  const doc = usePdf(f.url, estPdf(f))
  const [agrandi, setAgrandi] = useState(false)
  const pages = doc && doc !== 'erreur' ? Math.min(doc.numPages, PAGES_MAX) : 0
  const premiere = plage?.debut ?? 1
  const derniere = plage ? Math.min(plage.fin, pages) : pages
  return (
    <section className="carte apercu-document" aria-label="Aperçu de la pièce">
      <div className="apercu-document-barre">
        <strong className="visionneuse-titre">{f.nom}</strong>
        {doc && doc !== 'erreur' && (
          <span className="discret">
            {plage
              ? `Pages ${plage.debut} à ${plage.fin} sur ${doc.numPages}`
              : `${doc.numPages} page${doc.numPages > 1 ? 's' : ''}`}
          </span>
        )}
        <a className="bouton-discret hors-pwa" href={f.url} target="_blank" rel="noreferrer">
          Nouvel onglet
        </a>
        <button type="button" className="bouton-discret" onClick={() => setAgrandi(true)}>
          Agrandir
        </button>
      </div>
      <div ref={corps} className="apercu-document-corps">
        {estXml(f) && <TexteXml url={f.url} />}
        {estImage(f) && <img className="apercu-page" src={f.url} alt={f.nom} />}
        {!estPdf(f) && !estXml(f) && !estImage(f) && (
          <p className="discret">Pas d’aperçu pour ce type de fichier : l’ouvrir.</p>
        )}
        {estPdf(f) && doc === null && <p className="discret">Chargement…</p>}
        {doc === 'erreur' && <p className="alerte">Aperçu indisponible.</p>}
        {doc &&
          doc !== 'erreur' &&
          largeur > 0 &&
          Array.from({ length: Math.max(0, derniere - premiere + 1) }, (_, i) => (
            <Page key={premiere + i} doc={doc} numero={premiere + i} largeur={largeur} />
          ))}
        {doc && doc !== 'erreur' && !plage && doc.numPages > PAGES_MAX && (
          <p className="discret">
            {PAGES_MAX} pages sur {doc.numPages} : la suite dans un nouvel onglet.
          </p>
        )}
      </div>
      {agrandi && <Visionneuse f={f} onFermer={() => setAgrandi(false)} />}
    </section>
  )
}

/** `liensMontres` : mails déjà proposés sur la fiche, pas répétés sous leurs pièces. */
export function ApercusPieces({
  pieces,
  liensMontres = [],
}: {
  pieces: PieceTache[]
  liensMontres?: string[]
}) {
  const [ouverte, setOuverte] = useState<PieceTache | null>(null)
  return (
    <>
      <div className="apercus">
        {pieces.map((p) => (
          <figure key={p.id} className="apercu">
            <button
              type="button"
              className="apercu-vignette"
              onClick={() => setOuverte(p)}
              aria-label={`Voir ${p.nomFichier ?? 'la pièce'}`}
            >
              <Vignette piece={p} />
            </button>
            <figcaption>
              <Link to="/pieces/$id" params={{ id: p.id }}>
                {LIBELLES_TYPE_PIECE[p.type]}
                {p.emetteur && ` ${p.emetteur}`}
              </Link>
              <div className="discret">
                {[p.numero, p.dateDocument ? formaterJour(p.dateDocument) : null, p.nomFichier]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {p.lienMail && !liensMontres.includes(p.lienMail) && (
                <BoutonMail lien={p.lienMail} discret />
              )}
            </figcaption>
          </figure>
        ))}
      </div>
      {ouverte && <Visionneuse f={fichierDePiece(ouverte)} onFermer={() => setOuverte(null)} />}
    </>
  )
}
